import React, { useState, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, Target, TrendingUp, Users, CheckCircle, ArrowRight, Sparkles, Lightbulb } from 'lucide-react';
import { PageBanner } from '../components/shared/PageBanner';
import { useLearningModule, useProgressInsights } from '../hooks/useEducationContent';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { PathCard } from '../components/education/PathCard';

export function EducationHub() {
  const { pathId, moduleId } = useParams<{ pathId?: string; moduleId?: string }>();
  const [activeTab, setActiveTab] = useState<'paths' | 'modules' | 'progress'>('paths');

  // Simple mock data to avoid complex content generation
  const mockLearningPaths = [
    {
      id: 'beginner-path',
      title: 'Understanding Currency Markets',
      description: 'Learn the fundamentals of how currency markets work and economic factors that influence exchange rates',
      level: 'beginner' as const,
      modules: ['forex-basics', 'market-analysis', 'risk-management'],
      estimatedDuration: 180,
      completionCriteria: [
        'Complete all 3 modules',
        'Understand key economic concepts',
        'Recognize market factors and indicators'
      ]
    },
    {
      id: 'intermediate-path',
      title: 'Advanced Market Analysis',
      description: 'Deepen your understanding of market dynamics and economic analysis',
      level: 'intermediate' as const,
      modules: ['advanced-analysis', 'psychology', 'strategies'],
      estimatedDuration: 240,
      completionCriteria: [
        'Complete all modules',
        'Understand advanced economic indicators',
        'Analyze market trends and patterns'
      ]
    }
  ];

  const mockModules = [
    {
      id: 'forex-basics',
      title: 'Foreign Exchange Market Fundamentals',
      description: 'Understanding how foreign exchange markets work and factors affecting currency values',
      level: 'beginner' as const,
      duration: 45,
      prerequisites: [],
      objectives: [
        'Understand how forex markets function',
        'Learn about currency pairs and exchange rate quotes',
        'Know the structure of Nigerian forex markets'
      ],
      content: `# Foreign Exchange Market Fundamentals

## What is the Foreign Exchange Market?

The foreign exchange (forex) market is where currencies are bought and sold. It's the world's largest financial market, facilitating international trade and investment. Understanding how this market works is essential for comprehending global economics.

## Understanding Currency Pairs and Exchange Rates

### How Exchange Rates Work
Exchange rates represent the value of one currency in terms of another. For example:
- **USD/NGN**: Shows how many Naira equal one US Dollar
- **EUR/NGN**: Shows how many Naira equal one Euro
- **GBP/NGN**: Shows how many Naira equal one British Pound

### Nigerian Market Structure
Nigeria has both official exchange rates (set by the Central Bank) and parallel market rates. Understanding these differences is important for economic literacy.

## Key Educational Takeaways

1. Exchange rates reflect the relative value between currencies
2. Market rates fluctuate based on supply and demand
3. Economic factors influence currency values
4. Understanding market structure helps interpret economic news

**Disclaimer:** This content is for educational purposes only and does not constitute financial or trading advice.`
    },
    {
      id: 'market-analysis',
      title: 'Understanding Market Analysis',
      description: 'Learn how to read and interpret market trends and economic indicators',
      level: 'beginner' as const,
      duration: 60,
      prerequisites: ['forex-basics'],
      objectives: [
        'Understand fundamental and technical analysis concepts',
        'Read and interpret basic price charts',
        'Recognize Nigerian economic indicators'
      ],
      content: `# Understanding Market Analysis

## Types of Market Analysis (Educational Overview)

### Fundamental Analysis
Understanding economic factors that influence currency values:
- **Economic Indicators**: GDP growth, inflation rates, employment data
- **Central Bank Policies**: How CBN interest rates affect the Naira
- **Political Factors**: Government policies and stability
- **Trade Balance**: Imports and exports impact on currency

### Technical Analysis
Learning to read price charts and patterns:
- **Price Trends**: How to identify upward, downward, or sideways movements
- **Chart Reading**: Understanding what price movements represent
- **Historical Patterns**: Recognizing recurring market behaviors

## Educational Purpose
This knowledge helps you understand economic news and market reports. Always consult with licensed financial advisors before making any financial decisions.

**Disclaimer:** This is educational content only and not investment or trading advice.`
    },
    {
      id: 'risk-management',
      title: 'Understanding Financial Risk',
      description: 'Learn about different types of financial risks and how they affect currency markets',
      level: 'intermediate' as const,
      duration: 50,
      prerequisites: ['forex-basics'],
      objectives: [
        'Understand different types of financial risks',
        'Learn how risk affects currency values',
        'Recognize risk factors in economic news'
      ],
      content: `# Understanding Financial Risk

## What is Financial Risk?

Financial risk refers to the possibility of losing money or experiencing financial loss. Understanding risk is fundamental to financial literacy.

## Types of Financial Risks in Currency Markets

### Currency Risk (Exchange Rate Risk)
When exchange rates fluctuate, the value of money in different currencies changes. This affects:
- International businesses and trade
- Foreign investments
- Remittances and transfers

### Economic Risk
Broader economic changes that affect currency values:
- Inflation and purchasing power
- Economic growth and recession
- Government debt and fiscal policy

## Why Understanding Risk Matters

Financial literacy includes understanding how risks work in the economy. This knowledge helps you:
- Understand economic news better
- Make informed decisions about money
- Recognize factors affecting exchange rates

**Important:** This is educational content about financial concepts. For personal financial decisions, always consult with licensed financial advisors.`
    },
    {
      id: 'advanced-analysis',
      title: 'Advanced Market Analysis Concepts',
      description: 'Understanding advanced economic indicators and market analysis techniques',
      level: 'advanced' as const,
      duration: 75,
      prerequisites: ['market-analysis', 'risk-management'],
      objectives: [
        'Understand advanced economic indicators',
        'Read complex market data and charts',
        'Interpret multiple economic factors together'
      ],
      content: `# Advanced Market Analysis Concepts

## Advanced Economic Indicators

### Moving Averages
Understanding how economists and analysts use moving averages to identify long-term trends in economic data.

### Market Momentum Indicators
Learning how market momentum is measured and what it tells us about economic conditions.

### Economic Cycles
Understanding how economies move through different phases and what this means for currency values.

## Multi-Factor Analysis

Understanding how multiple economic factors work together:
- Interest rates and inflation
- Trade balance and GDP
- Political events and market sentiment

## Educational Application
This knowledge helps you better understand economic reports, news analysis, and market commentary.

**Disclaimer:** This content teaches analytical concepts for educational purposes only, not for making trading decisions.`
    },
    {
      id: 'psychology',
      title: 'Financial Decision-Making Psychology',
      description: 'Understanding how emotions and psychology affect financial decisions',
      level: 'intermediate' as const,
      duration: 55,
      prerequisites: ['forex-basics', 'risk-management'],
      objectives: [
        'Recognize emotional biases in financial decisions',
        'Understand behavioral economics concepts',
        'Learn about decision-making under uncertainty'
      ],
      content: `# Financial Decision-Making Psychology

## The Psychology of Money

Understanding how emotions influence financial decisions is an important part of financial literacy. Research shows that psychological factors often override logical reasoning.

## Common Psychological Biases

### Loss Aversion
People tend to feel losses more strongly than equivalent gains. Understanding this bias helps explain market behavior.

### Herd Mentality
The tendency to follow what others are doing, which can influence market movements.

### Confirmation Bias
Seeking information that confirms existing beliefs while ignoring contradictory evidence.

## Building Financial Discipline

Key principles of sound financial decision-making:
- Make decisions based on facts and research
- Understand your own emotional triggers
- Seek professional advice when needed
- Avoid impulsive financial decisions

**Educational Purpose:** This content teaches psychological concepts related to financial behavior, not trading strategies.`
    },
    {
      id: 'strategies',
      title: 'Market Analysis Frameworks',
      description: 'Understanding how different analytical approaches work in financial markets',
      level: 'advanced' as const,
      duration: 90,
      prerequisites: ['advanced-analysis', 'psychology'],
      objectives: [
        'Understand different analytical frameworks',
        'Learn how market analysis is conducted',
        'Recognize various economic approaches'
      ],
      content: `# Market Analysis Frameworks

## Understanding Analytical Approaches

Different analysts use various frameworks to understand market behavior. Learning about these approaches improves economic literacy.

### Time-Based Analysis
How different timeframes affect economic interpretation:
- **Short-term**: Daily and weekly economic data
- **Medium-term**: Monthly and quarterly reports
- **Long-term**: Annual trends and multi-year cycles

### Fundamental Economic Analysis
Framework for understanding economic conditions:
- GDP growth and economic health
- Inflation and purchasing power
- Employment and consumer spending
- Trade balance and foreign reserves

## Nigerian Economic Factors

Important factors affecting the Naira:
- Oil prices and export revenues
- Central Bank policies and interventions
- Foreign investment flows
- Government fiscal policies

## Educational Purpose
Understanding these frameworks helps you make sense of economic news and market reports. For actual financial decisions, consult licensed professionals.

**Important Disclaimer:** This content is strictly educational and does not provide financial, investment, or trading advice.`
    }
  ];

  // If viewing specific path
  if (pathId) {
    const selectedPath = mockLearningPaths.find(path => path.id === pathId);
    if (!selectedPath) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Learning Path Not Found</h2>
            <p className="text-gray-600 mb-4">The requested learning path could not be found.</p>
            <button
              onClick={() => window.history.back()}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <PageBanner 
          title={selectedPath.title}
          description={selectedPath.description}
          backgroundImage="https://images.unsplash.com/photo-1434030216411-0b793f4b4173"
        />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {selectedPath.modules.map((moduleId) => {
              const module = mockModules.find(m => m.id === moduleId);
              if (!module) return null;
              return (
                <div key={module.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        module.level === 'beginner' ? 'bg-green-100 text-green-800' :
                        module.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {module.level.charAt(0).toUpperCase() + module.level.slice(1)}
                      </span>
                      <div className="flex items-center text-gray-500 text-sm">
                        <BookOpen className="h-4 w-4 mr-1" />
                        {module.duration}m
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                    <p className="text-gray-600 mb-4">{module.description}</p>

                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-900 mb-2">What You'll Learn:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {module.objectives.slice(0, 3).map((objective, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-emerald-600 mt-1">•</span>
                            {objective}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to={`/education/module/${module.id}`}
                      className="mt-6 w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      Start Module
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // If viewing specific module - Use AI-generated content
  if (moduleId) {
    const staticModule = mockModules.find(module => module.id === moduleId);

    return <AIModuleViewer moduleId={moduleId} staticModule={staticModule} />;
  }

  // Mock progress data
  const mockProgress = {
    completedModules: 2,
    totalModules: mockModules.length,
    studyTime: 120,
    currentStreak: 3
  };

  const { data: progressInsights, isLoading: insightsLoading } = useProgressInsights(
    mockProgress,
    activeTab === 'progress'
  );

  const tabs = [
    { id: 'paths', label: 'Learning Paths', icon: Target },
    { id: 'modules', label: 'All Modules', icon: BookOpen },
    { id: 'progress', label: 'My Progress', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner
        title="Market and Trading Education Hub"
        description="Learn about currency markets with comprehensive educational courses"
        backgroundImage="https://images.unsplash.com/photo-1434030216411-0b793f4b4173"
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm p-2 mb-8">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Learning Paths Tab */}
        {activeTab === 'paths' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-2">Choose Your Learning Path</h2>
              <p className="text-gray-600">
                Structured learning journeys to help you understand currency markets and economic fundamentals
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mockLearningPaths.map((path) => (
                <PathCard
                  key={path.id}
                  id={path.id}
                  title={path.title}
                  description={path.description}
                  level={path.level}
                  estimatedDuration={path.estimatedDuration}
                  modulesCount={path.modules.length}
                  completionCriteria={path.completionCriteria}
                />
              ))}
            </div>
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-2">All Learning Modules</h2>
              <p className="text-gray-600">
                Individual modules you can take in any order based on your needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockModules.map((module) => (
                <div key={module.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                        module.level === 'beginner' ? 'bg-green-100 text-green-800 border-green-200' :
                        module.level === 'intermediate' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                        'bg-red-100 text-red-800 border-red-200'
                      }`}>
                        {module.level.charAt(0).toUpperCase() + module.level.slice(1)}
                      </span>
                      <div className="flex items-center text-gray-500 text-sm">
                        <BookOpen className="h-4 w-4 mr-1" />
                        {module.duration}m
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                    <p className="text-gray-600 mb-4">{module.description}</p>

                    {/* Prerequisites */}
                    {module.prerequisites.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Prerequisites:</h4>
                        <div className="flex flex-wrap gap-1">
                          {module.prerequisites.map((prereq) => (
                            <span key={prereq} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              {prereq.replace('-', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Learning Objectives */}
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4 text-emerald-600" />
                        <h4 className="text-sm font-medium text-gray-700">Learning Objectives:</h4>
                      </div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {module.objectives.slice(0, 3).map((objective, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-emerald-600 mt-1">•</span>
                            {objective}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to={`/education/module/${module.id}`}
                      className="w-full bg-emerald-600 text-white py-3 px-4 rounded-lg hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      Start Module
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-2">Your Learning Journey</h2>
              <p className="text-gray-600">
                Track your progress in understanding currency markets and economic concepts
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center gap-2 mb-6">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                    <h2 className="text-xl font-semibold">Your Learning Progress</h2>
                  </div>

                  {/* Overall Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Course Completion</span>
                      <span className="text-sm text-gray-600">{mockProgress.completedModules}/{mockProgress.totalModules} modules</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${(mockProgress.completedModules / mockProgress.totalModules) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{((mockProgress.completedModules / mockProgress.totalModules) * 100).toFixed(1)}% complete</p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-lg p-4 text-center">
                      <BookOpen className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-emerald-700">{mockProgress.completedModules}</div>
                      <div className="text-sm text-emerald-600">Modules Completed</div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <Target className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-blue-700">{Math.floor(mockProgress.studyTime / 60)}h</div>
                      <div className="text-sm text-blue-600">Study Time</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 space-y-6">
                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Completed: Risk Management Essentials</p>
                        <p className="text-sm text-green-600">2 hours ago</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Started: Market Analysis Fundamentals</p>
                        <p className="text-sm text-blue-600">Yesterday</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Progress Insights */}
                {progressInsights && (
                  <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg shadow-md p-6 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="h-6 w-6 text-emerald-600" />
                      <h3 className="text-lg font-semibold text-emerald-900">AI Learning Coach</h3>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed">{progressInsights.message}</p>
                      </div>

                      {progressInsights.suggestions && progressInsights.suggestions.length > 0 && (
                        <div className="bg-white rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="h-5 w-5 text-amber-600" />
                            <h4 className="font-semibold text-gray-900">Personalized Suggestions</h4>
                          </div>
                          <ul className="space-y-2">
                            {progressInsights.suggestions.map((suggestion: string, index: number) => (
                              <li key={index} className="flex items-start gap-2 text-gray-700">
                                <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <span>{suggestion}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="bg-emerald-600 text-white rounded-lg p-4">
                        <p className="font-medium italic">{progressInsights.motivation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {insightsLoading && (
                  <div className="bg-gray-50 rounded-lg shadow-md p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-20 bg-gray-200 rounded"></div>
                      <div className="h-32 bg-gray-200 rounded"></div>
                      <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                )}

                {/* Recommended Next Steps */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold mb-4">Recommended Next Steps</h3>
                  <div className="space-y-3">
                    <div className="p-4 border border-emerald-200 rounded-lg">
                      <h4 className="font-medium text-emerald-800 mb-1">Continue Current Module</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Complete "Market Analysis Fundamentals" - 30 minutes remaining
                      </p>
                      <Link
                        to="/education/module/market-analysis"
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                      >
                        Continue Learning →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AIModuleViewer({ moduleId, staticModule }: { moduleId: string; staticModule?: any }) {
  const [useAI, setUseAI] = useState(true);

  const topicMap: Record<string, { topic: string; level: 'beginner' | 'intermediate' | 'advanced' }> = {
    'forex-basics': { topic: 'Foreign Exchange Market Fundamentals and How Currency Markets Work', level: 'beginner' },
    'market-analysis': { topic: 'Understanding Market Analysis and Economic Indicators', level: 'beginner' },
    'risk-management': { topic: 'Understanding Financial Risk in Currency Markets', level: 'intermediate' },
    'advanced-analysis': { topic: 'Advanced Economic Indicators and Market Analysis Concepts', level: 'advanced' },
    'psychology': { topic: 'Financial Decision-Making Psychology and Behavioral Economics', level: 'intermediate' },
    'strategies': { topic: 'Market Analysis Frameworks and Economic Approaches', level: 'advanced' }
  };

  const moduleConfig = topicMap[moduleId];
  const { data: aiModule, isLoading, error } = useLearningModule(
    moduleConfig?.topic || 'Forex Trading Basics',
    moduleConfig?.level || 'beginner',
    useAI && !!moduleConfig
  );

  const displayModule = useAI && aiModule ? aiModule : staticModule;

  if (!displayModule && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Generating personalized learning content...</p>
        </div>
      </div>
    );
  }

  if (!displayModule) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Module Not Found</h2>
          <p className="text-gray-600 mb-4">The requested learning module could not be found.</p>
          <Link
            to="/education-hub"
            className="inline-block bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700"
          >
            Back to Learning Hub
          </Link>
        </div>
      </div>
    );
  }

  const renderMarkdownContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('# ')) {
        elements.push(
          <h1 key={index} className="text-3xl font-bold mb-6 text-gray-900">
            {trimmedLine.replace('# ', '')}
          </h1>
        );
      } else if (trimmedLine.startsWith('## ')) {
        elements.push(
          <h2 key={index} className="text-2xl font-semibold mb-4 text-gray-800 mt-8">
            {trimmedLine.replace('## ', '')}
          </h2>
        );
      } else if (trimmedLine.startsWith('### ')) {
        elements.push(
          <h3 key={index} className="text-xl font-semibold mb-3 text-gray-700 mt-6">
            {trimmedLine.replace('### ', '')}
          </h3>
        );
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        elements.push(
          <li key={index} className="ml-6 mb-2 text-gray-700 leading-relaxed">
            {trimmedLine.replace(/^[-*] /, '')}
          </li>
        );
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        elements.push(
          <p key={index} className="mb-3 font-semibold text-gray-800">
            {trimmedLine.replace(/\*\*/g, '')}
          </p>
        );
      } else if (trimmedLine === '') {
        elements.push(<div key={index} className="h-2" />);
      } else if (trimmedLine.length > 0) {
        const processedLine = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <p key={index} className="mb-4 text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: processedLine }} />
        );
      }
    });

    return elements;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner
        title={displayModule.title}
        description={displayModule.description}
        backgroundImage="https://images.unsplash.com/photo-1434030216411-0b793f4b4173"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {useAI && aiModule && (
          <div className="mb-6 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <Sparkles className="h-5 w-5" />
              <span className="font-medium">AI-Generated Content</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              This module content is personalized and generated in real-time using OpenAI
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-8">
            {displayModule.objectives && displayModule.objectives.length > 0 && (
              <div className="mb-8 p-6 bg-emerald-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Learning Objectives
                </h3>
                <ul className="space-y-2">
                  {displayModule.objectives.map((objective: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              {renderMarkdownContent(displayModule.content)}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <Link
                  to="/education-hub"
                  className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  Back to Learning Hub
                </Link>

                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  Back to top ↑
                </button>
              </div>
            </div>
          </div>
        </div>

        {displayModule.duration && (
          <div className="mt-6 text-center text-gray-600 text-sm">
            Estimated completion time: {displayModule.duration} minutes
          </div>
        )}
      </div>
    </div>
  );
}